// @flow

export function base64UrlToUint8Array(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
  const binary = atob(padded);
  return new Uint8Array(Array.from(binary, char => char.charCodeAt(0)));
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  const base64 = btoa(binary);
  let end = base64.length;
  while (end > 0 && base64[end - 1] === '=') {
    end--;
  }
  return base64.substring(0, end).replace(/\+/g, '-').replace(/\//g, '_');
}

export type RegistrationOptions = {|
  +rp: {| +name: string, +id?: string |},
  +user: {| +id: string, +name: string, +displayName: string |},
  +challenge: string,
  +pubKeyCredParams: $ReadOnlyArray<{| +type: string, +alg: number |}>,
  +timeout?: number,
  +excludeCredentials?: $ReadOnlyArray<{| +id: string, +type: string |}>,
  +authenticatorSelection?: {|
    +authenticatorAttachment?: string,
    +requireResidentKey?: boolean,
    +residentKey?: string,
    +userVerification?: string,
  |},
  +attestation?: string,
|};

export type CredentialResponse = {|
  +credentialId: string,
  +rawCredentialId: string,
  +credentialType: string,
  +authenticatorAttachment: string,
  +clientExtensionResults: { ... },
  +response: {|
    +clientDataJSON: string,
    +attestationObject: string,
  |},
|};

export function buildCredentialCreationOptions(registrationOptions: RegistrationOptions): { ... } {
  const excludeCredentials = registrationOptions.excludeCredentials
    ? registrationOptions.excludeCredentials.map(cred => ({
      id: base64UrlToUint8Array(cred.id),
      type: cred.type,
    }))
    : undefined;

  return {
    rp: {
      name: registrationOptions.rp.name,
      id: registrationOptions.rp.id || 'ebanx.com',
    },
    user: {
      name: registrationOptions.user.name,
      displayName: registrationOptions.user.displayName,
      id: base64UrlToUint8Array(registrationOptions.user.id),
    },
    challenge: base64UrlToUint8Array(registrationOptions.challenge),
    pubKeyCredParams: registrationOptions.pubKeyCredParams,
    timeout: registrationOptions.timeout,
    excludeCredentials,
    authenticatorSelection: registrationOptions.authenticatorSelection,
    attestation: registrationOptions.attestation,
  };
}

// $FlowFixMe[unclear-type] — credential is a PublicKeyCredential from navigator.credentials.create()
export function extractCredentialResponse(credential: Object): CredentialResponse {
  return {
    credentialId: credential.id,
    rawCredentialId: arrayBufferToBase64Url(credential.rawId),
    credentialType: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || 'platform',
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(credential.response.attestationObject),
    },
  };
}

export function getWebAuthnErrorMessage(error: Error): string {
  switch (error.name) {
    case 'NotAllowedError':
      return 'Autentica\u00E7\u00E3o cancelada ou negada.';
    case 'NotSupportedError':
      return 'Seu navegador n\u00E3o suporta autentica\u00E7\u00E3o biom\u00E9trica.';
    case 'SecurityError':
      return 'Erro de seguran\u00E7a. Verifique o dom\u00EDnio.';
    default:
      if (error.message && error.message.includes('timeout')) {
        return 'Tempo limite de autentica\u00E7\u00E3o excedido.';
      }
      return error.message || 'Falha na autentica\u00E7\u00E3o.';
  }
}
